// Pure builder for the Order / PO PDF as a pdfmake document definition.
// Produces REAL VECTOR TEXT (selectable + searchable), replacing the raster
// image PDF html2pdf used to produce from the old OrderPrintLayout (since
// removed). This doc backs the unified "Download / Print" button
// (OrderPdfButton → SharePdfButton) — Download, Print, and Share all render it.
//
// No dependency on pdfmake itself — only declarative doc-def data is returned,
// so the layout stays pure and unit-testable (see orderPdf.test.js). Mirrors
// the conventions in budgetReportPdf.js / gradingPdf.js.

import { formatCurrency, formatCurrencyExt } from './formatCurrency';
import { formatReadableDate } from './dateFormat';
import { getOrderNumber } from './orderTitle';
import { poDisplayText, orderIdentityLine } from './orderPdfMeta';
import { INTERNAL_NOTES_LABEL } from '../constants/orderNotes';
import { cap } from './listPdf';
import { itemCategoryLabel } from './orderFormHelpers';

const NAVY = '#1e3a5f';
const ZEBRA = '#f8f8f8';
const SECONDARY_FG = '#666666';
const GREY_RULE = '#dddddd';
const BOX_RULE = '#cccccc';
const LABEL_GREY = '#666666';
const FOOTER_GREY = '#999999';

// A4 portrait printable width ≈ 595 − (36 + 36) margins. Used for the header
// rule; kept under the content width so the canvas line never overflows.
const HEADER_LINE_WIDTH = 523;

// Urgency labels — covers BOTH key conventions seen in real order data: the
// `2_3_days`/`7_days` style (constants/colors.js) AND the `within_*` style
// (the old OrderPrintLayout map). Orders are stored with either depending on
// when/where they were created, so map both to a friendly label rather than
// fall through to the raw enum (e.g. "2_3_days").
const urgencyLabels = {
  same_day: 'Same Day',
  next_day: 'Next Day',
  '2_3_days': 'Within 2-3 Days',
  '7_days': 'Within 7 Days',
  within_2_3_days: 'Within 2-3 Days',
  within_7_days: 'Within 7 Days',
  custom: 'Custom Date',
};

const footerDate = () => new Date().toLocaleDateString('en-GB', {
  day: '2-digit', month: 'long', year: 'numeric',
});

// 🔻 `headerTitleFor(order, isAdmin, isBuyer)` was DELETED here on 2026-08-12.
// It read `(isAdmin || isBuyer) && po ? \`PO:${po}\` : getOrderRef(order)`, so a
// PO REPLACED the reference for buyers/admins and the identifier was role-gated.
// The order/PO identity is now `orderIdentityLine(order)` — both, for everyone,
// with the order NUMBER (`ORD-004`) in place of the production reference. Do not
// re-couple the identifier to a role flag; `isAdmin` survives ONLY to gate the
// Admin Notes block, which a supplier must not read.

/**
 * The value of the deadline meta box.
 *
 * 🔀 RE-POINTED AND RELABELLED 2026-08-09 (lead-time increment 3). The box was
 * labelled "Urgency" and read `orders.urgency` alone, returning `'N/A'` when
 * absent — so once nothing writes that column, every new order's PDF would have
 * carried a meta box reading **URGENCY: N/A**, on the one document that leaves
 * the building and goes to a supplier.
 *
 * 🔑 **The DATE now leads, because the date is the fact.** For an order raised
 * after increment 3 that is all there is; for a legacy order the label is
 * appended after it so nothing a supplier used to read has been taken away.
 * ⚠️ The old code showed the date ONLY for `urgency === 'custom'` — one of the
 * seven guards — so a legacy order with a date and any other urgency printed
 * the category and hid the actual deadline. That is fixed here rather than
 * merely preserved.
 */
function neededByValue(order) {
  const parts = [];
  if (order.custom_needed_by_date) parts.push(formatReadableDate(order.custom_needed_by_date));
  if (order.urgency) parts.push(urgencyLabels[order.urgency] || order.urgency);
  if (parts.length === 0) return 'N/A';
  // "12 August 2026 (Within 7 Days)" on a legacy order; the bare date on a new
  // one; the bare label on a legacy order that never had a date.
  return parts.length === 2 ? `${parts[0]} (${parts[1]})` : parts[0];
}

// Segmented, bordered single-row table — the "meta boxes" strip.
function metaRow(boxes) {
  const widths = boxes.map(() => '*');
  const row = boxes.map(b => ({
    stack: [
      { text: String(b.label).toUpperCase(), fontSize: 7, bold: true, color: LABEL_GREY, margin: [0, 0, 0, 2] },
      { text: b.value, fontSize: 9, bold: !!b.bold, color: '#000' },
    ],
  }));
  return {
    table: { widths, body: [row] },
    layout: {
      hLineColor: () => BOX_RULE, vLineColor: () => BOX_RULE,
      hLineWidth: () => 0.5, vLineWidth: () => 0.5,
      paddingTop: () => 5, paddingBottom: () => 5, paddingLeft: () => 7, paddingRight: () => 7,
    },
    margin: [0, 0, 0, 10],
  };
}

/**
 * Label/value grid (borderless) for order details.
 *
 * 🔑 TAKES GROUPS, NOT A FLAT LIST, AND THE GROUP BOUNDARY IS THE ONE THING A
 * COLUMN COUNT CANNOT EXPRESS.  A group's items wrap within the group at
 * `columns`; the NEXT group always starts a fresh row.  That is what lets the
 * My Space variant put Supplier "on its own row" (owner, 2026-09-11) without a
 * padding item, a sentinel value or a second copy of this function.
 *
 * 🪤 `columns` DRIVES THREE THINGS AND MISSING ANY ONE SHIPS A RAGGED TABLE:
 * the `widths` array, the per-row cell count, AND the empty-body fallback.  All
 * three were hard-coded 3-wide before 2026-09-11, and a `widths` of four over
 * rows of three is a perfectly valid pdfmake table that simply prints wrong.
 * ⚠️ Two of the three are bite-proven (hard-coding `widths` back to three reds
 * §1 alone; `group.slice(…).map` in place of the padded `Array.from` reds §1
 * and §6).  The THIRD — the empty-body fallback — is NOT, and saying so is
 * better than implying coverage: both call sites always supply at least four
 * items, so that branch is unreachable from this file today and no test can
 * reach it.  It follows `columns` for the helper's own correctness, not because
 * anything fails if it does not.
 *
 * 🟢 THE THREE-COLUMN DEPARTMENT PATH IS BYTE-IDENTICAL, which is a property of
 * the defaults rather than a claim: one group of the same items at `columns = 3`
 * chunks exactly as the old `i += 3` loop did, and `Array(3).fill('*')` is
 * `['*','*','*']`.  Pinned by §10 of `orderPdf.test.js`, which compares the
 * WHOLE serialised content for a department order against the pre-feature shape.
 */
function detailsGrid(groups, columns = 3) {
  const cell = (it) => (it ? {
    stack: [
      { text: String(it.label).toUpperCase(), fontSize: 7, bold: true, color: LABEL_GREY, margin: [0, 0, 0, 1] },
      { text: it.value, fontSize: 9, color: '#000' },
    ],
    margin: [0, 0, 0, 5],
  } : {});
  const rows = [];
  for (const group of groups) {
    for (let i = 0; i < group.length; i += columns) {
      // 🪤 `Array.from` with a length, never `group.slice(i, i + columns).map`:
      // a short final chunk must be PADDED with empty cells, and `slice` yields
      // however many exist. pdfmake does not pad, it prints a short row.
      rows.push(Array.from({ length: columns }, (_, k) => cell(group[i + k])));
    }
  }
  return {
    table: {
      widths: Array.from({ length: columns }, () => '*'),
      body: rows.length ? rows : [Array.from({ length: columns }, () => ({}))],
    },
    layout: 'noBorders',
    margin: [0, 0, 0, 8],
  };
}

const notesBlock = (heading, body) => ({
  stack: [
    { text: heading, fontSize: 10, bold: true, color: '#333', margin: [0, 0, 0, 3] },
    { text: String(body), fontSize: 9, color: '#444', preserveLeadingSpaces: true },
  ],
  margin: [0, 0, 0, 10],
});

/**
 * THE label for a file attached to a LINE ITEM: `Item 2 (SF-25)`.
 *
 * 🔑 EXTRACTED 2026-08-11, NOT INVENTED — this is the format `attachedFilesBlock`
 * below has already shipped in the order PDF, lifted verbatim so that the
 * expanded order row could name the line a file belongs to without a second
 * answer to the same question (Code Unification Principle).  Its second consumer
 * is `components/OrderExpandedDetails.js`, which lists item-level attachments in
 * the order's Attachments section rather than grouping them under each line —
 * see that file for why grouping is not available to it.
 *
 * ⚠️ `index` is the item's ZERO-BASED position in `order.items` and the label is
 * ONE-BASED, because "Item 1" is what a human counting down the order says.
 * 🪤 `part_number` WINS over `description`, which is the opposite of what the
 * table cells do — a part number is the shorter, more precise identifier and
 * this label sits inline beside a filename where a long description would push
 * the name out of view.  The `Item N` prefix is what makes the fallback safe:
 * even with neither field the row still says which line it is.
 */
export function orderItemFileLabel(item, index) {
  const n = index + 1;
  const detail = (item && (item.part_number || item.description)) || null;
  return detail ? `Item ${n} (${detail})` : `Item ${n}`;
}

// Lists the NAMES of files attached to the order (order-level + per line item)
// so a reader of the PDF knows attachments exist in Crew Forge even though the
// document can't embed them — they can ask the requester for copies. Returns
// null when the order has no files. `order.files` comes from GET /api/orders/:id
// (every order_files row: file_name, order_item_id = null for order-level).
function attachedFilesBlock(order) {
  const all = order.files || [];
  if (!all.length) return null;
  const items = order.items || [];

  const orderLevel = all.filter(f => f.order_item_id == null).map(f => f.file_name);
  const byItem = {};
  all.filter(f => f.order_item_id != null).forEach(f => {
    (byItem[f.order_item_id] = byItem[f.order_item_id] || []).push(f.file_name);
  });

  const lines = [];
  if (orderLevel.length) {
    lines.push({ text: [{ text: 'Order: ', bold: true }, { text: orderLevel.join(', ') }], fontSize: 8, margin: [0, 1, 0, 0] });
  }
  items.forEach((item, idx) => {
    const names = byItem[item.id];
    if (names && names.length) {
      // `orderItemFileLabel` is this block's own former expression, extracted so
      // the expanded order row can render the same attribution. 🪤 It fixes a
      // latent oddity in the original: the old fallback was
      // `part_number || description || \`Item ${idx + 1}\``, which on an item
      // with neither field produced the doubled `Item 3 (Item 3)`. The helper
      // drops the parenthetical instead.
      lines.push({ text: [{ text: `${orderItemFileLabel(item, idx)}: `, bold: true }, { text: names.join(', ') }], fontSize: 8, margin: [0, 1, 0, 0] });
    }
  });
  if (!lines.length) return null; // files exist but couldn't be attributed — shouldn't happen

  const count = all.length;
  return {
    stack: [
      { text: 'Attached Files', fontSize: 11, bold: true, color: NAVY, margin: [0, 8, 0, 4] },
      {
        text: `${count} file${count === 1 ? '' : 's'} stored against this order in Crew Forge (not included in this document) — contact the order requester for copies.`,
        fontSize: 8, italics: true, color: SECONDARY_FG, margin: [0, 0, 0, 4],
      },
      ...lines,
    ],
    margin: [0, 0, 0, 8],
  };
}

function lineItemsTable(items, fmt) {
  const hcell = (t, align = 'left') => ({ text: t, fillColor: NAVY, color: 'white', bold: true, fontSize: 8, alignment: align });
  const header = [
    hcell('#'), hcell('Part No.'), hcell('Description'),
    hcell('Qty', 'center'), hcell('Unit Price', 'right'),
    hcell('Line Total', 'right'), hcell('Needed By'), hcell('Est. Lead Time'),
  ];
  const body = [header];

  if (!items.length) {
    body.push([
      { text: 'No items', colSpan: 8, alignment: 'center', color: '#999', fontSize: 8 },
      {}, {}, {}, {}, {}, {}, {},
    ]);
  } else {
    items.forEach((item, index) => {
      const descStack = [{ text: item.description || '-', fontSize: 8 }];
      if (item.is_hire) {
        // 🔴 'Total' → 'Each' for the total rate type (2026-08-20). This is the
        // DUPLICATE of the rate that the Unit Price column already carries, and
        // since `hireCostForDays` multiplies every rate column by `quantity`, on
        // a quantity-3 line it printed "Total: £1,050" on a row whose own Total
        // column said £3,150 — inside the one document that leaves the building.
        //
        // 🟢 THE TABLE ITSELF IS ALREADY SELF-CONSISTENT AND IS DELIBERATELY NOT
        // TOUCHED — a non-obvious consequence of the quantity fix that is worth
        // knowing before anyone "corrects" it: for a hire line the Unit Price
        // column IS `total_hire_rate`, so Qty × Unit Price now reconciles to
        // `line_total` exactly (3 × £1,050 = £3,150). Today's change made that
        // column CORRECT, not wrong. Only this description-stack copy misnamed
        // the figure, so only this label moves. 'Daily'/'Weekly' read as unit
        // rates and stay; see `HireCard.js#getRateDisplay` for the reasoning.
        const rateLabel = item.hire_rate_type === 'daily' ? 'Daily' : item.hire_rate_type === 'weekly' ? 'Weekly' : 'Each';
        const ratePart = item.total_hire_rate ? ` | ${rateLabel}: ${fmt(item.total_hire_rate)}` : '';
        descStack.push({
          text: `HIRE: ${formatReadableDate(item.hire_from) || '?'} to ${formatReadableDate(item.hire_to) || '?'}${ratePart}`,
          fontSize: 7, italics: true, color: SECONDARY_FG, margin: [0, 1, 0, 0],
        });
      }
      if (item.item_category) {
        descStack.push({ text: `Category: ${itemCategoryLabel(item.item_category)}`, fontSize: 7, color: SECONDARY_FG, margin: [0, 1, 0, 0] });
      }
      const unitPrice = item.is_hire
        ? (item.total_hire_rate ? fmt(item.total_hire_rate) : '-')
        : fmt(item.unit_price);
      body.push([
        { text: String(index + 1), fontSize: 8 },
        { text: item.part_number || '-', fontSize: 8 },
        { stack: descStack },
        { text: item.quantity != null ? String(item.quantity) : '', alignment: 'center', fontSize: 8 },
        { text: unitPrice, alignment: 'right', fontSize: 8 },
        { text: fmt(item.line_total), alignment: 'right', fontSize: 8, bold: true },
        { text: formatReadableDate(item.needed_by_date) || '-', fontSize: 8 },
        { text: item.lead_time || '-', fontSize: 8 },
      ]);
      // Item notes get a full-width row beneath the item — the Description cell
      // is too narrow for anything beyond a short line. Explicit fillColor so it
      // reads as a sub-row of its item (overrides the zebra layout fill).
      if (item.item_notes) {
        body.push([
          {
            colSpan: 8,
            text: [{ text: 'Note: ', bold: true }, { text: String(item.item_notes) }],
            fontSize: 7.5, italics: true, color: SECONDARY_FG, fillColor: '#f3f4f6',
          },
          {}, {}, {}, {}, {}, {}, {},
        ]);
      }
    });
  }

  return {
    table: { headerRows: 1, widths: [16, 50, '*', 26, 52, 56, 58, 52], body, dontBreakRows: true },
    layout: {
      fillColor: (rowIndex) => (rowIndex > 0 && rowIndex % 2 === 0 ? ZEBRA : null),
      hLineColor: () => GREY_RULE,
      vLineColor: () => GREY_RULE,
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      paddingTop: () => 3,
      paddingBottom: () => 3,
      paddingLeft: () => 4,
      paddingRight: () => 4,
    },
    margin: [0, 0, 0, 8],
  };
}

/**
 * 🔻 **THE `includeVat` BRANCH BELOW IS UNREACHABLE FROM THE APP, AND WAS
 * ALREADY UNREACHABLE BEFORE THE 2026-08-08 VAT REMOVAL — do not read it as
 * something that change broke.** `components/OrderPdfButton.js` calls
 * `buildOrderDoc({ order, supplierContact, isAdmin, isBuyer })` and has never
 * passed `includeVat`, which defaults to `false` in `buildOrderContent`. **The
 * order PDF has therefore always been ex-VAT-only in the running app**; the
 * `true` branch is exercised only by `orderPdf.test.js`. It is KEPT because the
 * owner asked to keep the code.
 *
 * 🔑 What DID change on 2026-08-08 is the `else` label — the live one, on the
 * most outward-facing artefact this app produces (it goes to suppliers). It read
 * "Total (Ex VAT):" and now reads "Total:", because a department may now type
 * inc-VAT prices and nothing records which, so the app cannot assert either.
 * The `true` branch keeps "Grand Total (Inc VAT):" and that is consistent, not
 * an oversight: if the branch is ever reached the figure really would be
 * inc-VAT. Full reasoning in `client/src/hooks/useVatMode.js`.
 */
function totalsBlock(subtotal, includeVat) {
  const totRow = (label, val, grand = false) => ([
    { text: label, fontSize: grand ? 10 : 9, bold: grand, color: grand ? 'white' : '#000', fillColor: grand ? NAVY : null, alignment: 'left' },
    { text: val, fontSize: grand ? 10 : 9, bold: grand, color: grand ? 'white' : '#000', fillColor: grand ? NAVY : null, alignment: 'right' },
  ]);
  const rows = [];
  if (includeVat) {
    const vat = subtotal * 0.2;
    rows.push(totRow('Subtotal:', formatCurrency(subtotal)));
    rows.push(totRow('VAT (20%):', formatCurrency(vat)));
    rows.push(totRow('Grand Total (Inc VAT):', formatCurrency(subtotal + vat), true));
  } else {
    rows.push(totRow('Total:', formatCurrency(subtotal), true));
  }
  return {
    columns: [
      { width: '*', text: '' },
      {
        width: 200,
        table: { widths: ['*', 'auto'], body: rows },
        layout: {
          hLineColor: () => GREY_RULE, vLineColor: () => GREY_RULE,
          hLineWidth: () => 0.5, vLineWidth: () => 0,
          paddingTop: () => 4, paddingBottom: () => 4, paddingLeft: () => 8, paddingRight: () => 8,
        },
      },
    ],
    margin: [0, 8, 0, 0],
  };
}

// ── Inner content (no page wrapper) ────────────────────────────────────────
// Returns just the pdfmake `content[]` slice for an order. Pure + testable.
export function buildOrderContent({ order, supplierContact = null, includeVat = false, isAdmin = false, isBuyer = false }) {
  if (!order) return [];
  const fmt = (a) => formatCurrencyExt(a, includeVat);
  const headerTitle = orderIdentityLine(order);
  const items = order.items || [];
  const subtotal = items.reduce((sum, it) => sum + (parseFloat(it.line_total) || 0), 0);

  // Header band
  const companyLine = order.company_name
    ? `${order.company_name}${order.production_name ? ` - ${order.production_name}` : ''}`
    : '';

  // Meta boxes
  const metaBoxes = [
    // 🔀 Caption moved WITH content (2026-08-12): a box captioned "Order
    // Reference" holding an order NUMBER is a caption that no longer describes
    // its contents, on the document a supplier reads. Change one, change both.
    { label: 'Order Number', value: getOrderNumber(order), bold: true },
    { label: 'Order Date', value: formatReadableDate(order.order_date || order.created_at) || 'N/A' },
    { label: 'Status', value: cap(order.status) || 'N/A' },
    // 🔻 Was `{ label: 'Urgency', value: urgencyValue(order) }` until 2026-08-09.
    // The label moved with the meaning: the order now has one needed-by date and
    // no urgency tier, and a box headed "Urgency" printing a date would be the
    // misleading half of the change rather than the useful half.
    { label: 'Needed By', value: neededByValue(order) },
  ];
  // 🪤 `poDisplayText`, never `String(order.po_number)` — a stored `PO-147`
  // must not print as the doubled `PO:PO-147`, and a legacy bare `147` must
  // print `PO-147`. Same rule as the footer beside it.
  const poText = poDisplayText(order);
  if (poText) metaBoxes.push({ label: 'PO Number', value: poText, bold: true });

  // ── Details grid ────────────────────────────────────────────────────────
  // 🔻 TWO LAYOUTS SINCE 2026-09-11, SELECTED BY A SERVER FLAG. Owner: *"Also
  // update JUST FOR MY SPACE PRINTS AND PDF: Remove 'Department' replace with
  // email. but change the label to just 'Email' not Buyer email. Phone goes
  // where Job/Rig/Gag is. Job/Rig/Gag goes next to phone. So 4 columns.
  // Supplier stays but now is on its own row"*.
  //
  // 🔑 `order.is_personal_workspace === true`, AND THE STRICT COMPARISON IS
  // LOAD-BEARING. The flag comes from `GET /api/orders/:id`, which resolves
  // `productions.is_personal` from the order's DEPARTMENT — INTEGER DEFAULT 0
  // on BOTH engines, so `pg` returns a number, never a BOOLEAN — and normalises it. A truthy `1` slipping through
  // must fall SAFE onto the department layout every order in the app has
  // printed for a year, not silently re-lay-out on one engine and not the
  // other, which is a defect that cannot reproduce in dev.
  //
  // 🪤 THIS FUNCTION CANNOT ANSWER THE QUESTION ITSELF and must never try: it
  // is pure over one `order`, the join is two tables deep, and `order.department`
  // is a display name, so any name- or department-shaped guess here would be
  // wrong for the very workspaces it was guessing about.
  const isPersonalWorkspace = order.is_personal_workspace === true;

  const supplierCell = { label: 'Supplier', value: `${order.supplier_name || 'N/A'}${order.only_use_this_supplier ? ' (Exclusive)' : ''}` };
  const supplierContactCells = [];
  if (supplierContact) {
    if (supplierContact.phone) supplierContactCells.push({ label: 'Supplier Phone', value: supplierContact.phone });
    if (supplierContact.email) supplierContactCells.push({ label: 'Supplier Email', value: supplierContact.email });
    if (supplierContact.account_number) supplierContactCells.push({ label: 'Account Number', value: supplierContact.account_number });
  }

  // MY SPACE: four columns, in the owner's own order, then Supplier alone on
  // the next row with its contact cells trailing it.
  //
  // 🔑 PHONE IS UNCONDITIONAL HERE AND CONDITIONAL BELOW, WHICH IS THE ONE
  // DELIBERATE ASYMMETRY BETWEEN THE TWO LISTS. On the department grid the
  // cells simply wrap, so omitting an absent Phone costs nothing. Here the
  // COLUMN POSITION is the requirement — "Phone goes where Job/Rig/Gag is.
  // Job/Rig/Gag goes next to phone" — so an omitted cell would slide Job / Rig
  // / Gag one column left and leave a hole at the end of the row. `'N/A'`
  // keeps the row four wide for every user.
  //
  // 🔻 NO DEPARTMENT CELL, AND THE ABSENCE IS THE DELIVERABLE: a My Space order
  // belongs to a personal workspace, so printing its department name on a
  // document a supplier reads was never useful. Pinned by §3 of the test file,
  // because an absence cannot be seen in a diff.
  const personalDetailGroups = [
    [
      { label: 'Requested By', value: order.requested_by || 'N/A' },
      { label: 'Email', value: order.buyer_email || 'N/A' },
      { label: 'Phone', value: order.user_phone || 'N/A' },
      { label: 'Job / Rig / Gag', value: order.job_rig || 'N/A' },
    ],
    [supplierCell, ...supplierContactCells],
  ];

  // DEPARTMENT: exactly the three-column list this file has always built.
  const details = [
    { label: 'Requested By', value: order.requested_by || 'N/A' },
    { label: 'Department', value: order.department || 'N/A' },
    // 🔻 `{ label: 'Personal Reference', value: order.personal_ref || 'N/A' }`
    // came off on 2026-08-13 — the ORDER-level personal_ref is retired (gone
    // from the title, search, and the form input in the same effort). This was
    // its last display surface. NOT the user-level GDPR `users.personal_ref`.
    { label: 'Job / Rig / Gag', value: order.job_rig || 'N/A' },
    supplierCell,
  ];
  details.push(...supplierContactCells);
  details.push({ label: 'Buyer Email', value: order.buyer_email || 'N/A' });
  if (order.user_phone) details.push({ label: 'Phone', value: order.user_phone });

  // MY SPACE "HEADER DETAILS" — trimmed at the ENDS only. Internal blank lines
  // are the user's own paragraph breaks and must survive; a whitespace-only
  // value is nothing and must print nothing (the same "blank means blank"
  // reading the form and the server both take).
  const headerDetailsText = typeof order.header_details === 'string'
    ? order.header_details.trim()
    : '';

  const content = [
    { text: 'ORDER REQUEST', fontSize: 18, bold: true, color: NAVY },
    // ── MY SPACE "HEADER DETAILS" (2026-09-11) ────────────────────────────
    // Owner: *"it will appear on the order (Print and PDF) under the ORDER
    // REQUEST title."* This position IS the requirement — directly beneath the
    // title and above the company line, so it reads as part of the letterhead
    // rather than as a note attached to the order body.
    //
    // 🔑 `headerDetailsText && {...}`, so an order without one contributes
    // NOTHING: `content.filter(Boolean)` at the end of this function drops the
    // `false`, and a department order's PDF is byte-identical to what it
    // printed before this feature existed. An empty-string node would instead
    // render a blank line and a 6pt margin under the title of every order in
    // the app — invisible in review, visible on every printout.
    //
    // 🪤 ONE node carrying the newlines, never a split into paragraphs: pdfmake
    // honours "\n" inside a `text` string, and splitting would lose the
    // grouping and insert margins the user did not ask for.
    // `preserveLeadingSpaces` matches `notesBlock` — an address block is
    // frequently indented and losing that would silently reformat it.
    //
    // 🎨 Deliberately quieter than `companyLine` below (9pt, grey) and with no
    // heading of its own: it is the user's own standing text, not a titled
    // section, and a bold "Header details" label above it would print a word
    // the supplier has no use for.
    headerDetailsText && {
      text: headerDetailsText,
      fontSize: 9,
      color: '#444',
      preserveLeadingSpaces: true,
      margin: [0, 4, 0, 0],
    },
    companyLine && { text: companyLine, fontSize: 12, bold: true, color: '#333', margin: [0, 2, 0, 0] },
    order.production_address && { text: order.production_address, fontSize: 9, color: '#555', margin: [0, 1, 0, 0] },
    { canvas: [{ type: 'line', x1: 0, y1: 0, x2: HEADER_LINE_WIDTH, y2: 0, lineWidth: 2, lineColor: NAVY }], margin: [0, 6, 0, 10] },
    metaRow(metaBoxes),
    isPersonalWorkspace ? detailsGrid(personalDetailGroups, 4) : detailsGrid([details]),
    order.notes && notesBlock('Order Notes', order.notes),
    { text: 'Line Items', fontSize: 11, bold: true, color: NAVY, margin: [0, 2, 0, 4] },
    lineItemsTable(items, fmt),
    totalsBlock(subtotal, includeVat),
    attachedFilesBlock(order),
    // 🔁 HEADING RENAMED 2026-09-13, `Admin Notes` → `Internal notes` — the
    // same rename the screen got (owner: *"update the label as you
    // recommended"*). This is the MANAGER's copy of the PDF; the block is still
    // gated on `isAdmin` and is still absent from every supplier PDF, which is
    // exactly what the field's new tooltip ("Not printed.") promises. The
    // server's audit wording is deliberately untouched: that is record, not a
    // label. String from `constants/orderNotes.js`, never retyped.
    (order.admin_notes && isAdmin) && notesBlock(INTERNAL_NOTES_LABEL, order.admin_notes),
  ];

  return content.filter(Boolean);
}

// ── Full doc wrapper ───────────────────────────────────────────────────────
export function buildOrderDoc(args) {
  const { order = {} } = args;
  const headerTitle = orderIdentityLine(order);
  return {
    pageSize: 'A4',
    pageOrientation: 'portrait',
    pageMargins: [36, 36, 36, 44],
    info: {
      title: headerTitle ? `Order ${headerTitle}` : 'Order',
      author: order.company_name || 'Crew Forge',
      subject: 'Order Request',
    },
    footer: (currentPage, pageCount) => ({
      columns: [
        {
          text: `Printed on ${footerDate()} | ${headerTitle} | Crew Forge`,
          fontSize: 7, color: FOOTER_GREY, alignment: 'left',
          margin: [36, 8, 0, 0],
        },
        {
          text: `Page ${currentPage} of ${pageCount}`,
          fontSize: 7, color: FOOTER_GREY, alignment: 'right',
          margin: [0, 8, 36, 0],
        },
      ],
    }),
    defaultStyle: { fontSize: 9, color: '#000' },
    content: buildOrderContent(args),
  };
}
